import React, { Children, cloneElement, forwardRef, isValidElement } from 'react';
import type { CSSProperties } from 'react';
import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';

type FormRowColumns = number[];

interface FormRowProps {
  cols?: FormRowColumns;
  hidden?: boolean;
  children: React.ReactNode;
  className?: string;
}

const V_SPACE = 3;
const H_SPACE = 2;
const SPLIT_H_SPACE = H_SPACE / 2;

const rootStyle = css`
  margin-bottom: ${theme.spacing(V_SPACE)};
`;

const flexRowStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-left: ${theme.spacing(-SPLIT_H_SPACE)};
  margin-right: ${theme.spacing(-SPLIT_H_SPACE)};

  & > * {
    margin-left: ${theme.spacing(SPLIT_H_SPACE)};
    margin-right: ${theme.spacing(SPLIT_H_SPACE)};
  }
`;

const equalColumnsStyle = css`
  & > * {
    flex-basis: calc(100% / var(--form-row-count));
  }
`;

const customColumnsStyle = css`
  & > * {
    flex-basis: var(--form-row-col-basis, auto);
  }
`;

const singleColumnStyle = css`
  & > * {
    width: calc(var(--form-row-single-basis) - ${SPLIT_H_SPACE}px);
  }
`;

export const FormRow = forwardRef<HTMLDivElement, FormRowProps>(
  ({ children, cols, hidden, className, ...rest }, ref) => {
    if (hidden) return null;

    const count = Children.toArray(children).length;
    const hasCols = !!cols?.length;

    const rowStyle: CSSProperties = {
      ...(count > 1 && !hasCols ? { '--form-row-count': count } : {}),
      ...(count === 1 && hasCols ? { '--form-row-single-basis': `${cols[0]}%` } : {}),
    } as CSSProperties;

    const content =
      count > 1 && hasCols
        ? Children.map(children, (child, idx) =>
            isValidElement(child) && idx < cols.length
              ? cloneElement(child as React.ReactElement<{ style?: CSSProperties }>, {
                  style: { ...(child.props.style ?? {}), '--form-row-col-basis': `${cols[idx]}%` } as CSSProperties,
                })
              : child
          )
        : children;

    return (
      <div
        ref={ref}
        className={cx(
          rootStyle,
          count > 1 && flexRowStyle,
          count > 1 && !hasCols && equalColumnsStyle,
          count > 1 && hasCols && customColumnsStyle,
          count === 1 && hasCols && singleColumnStyle,
          className
        )}
        style={rowStyle}
        {...rest}
      >
        {content}
      </div>
    );
  }
);
